// Locale-aware date format maps.
// Keys match BCP 47 language tags stored in workspace.config.locale.
// All formats use moment.js / dayjs token syntax.

export type DateFormats = {
  short: string;
  shortDateTime: string;
  readable: string;
  readableDateTime: string;
  readableDateTimeFilename: string;
  columnHeader: string;
  chartDate: string;
  /** Month bucket labels on time-series charts (no day). */
  monthYear: string;
  dayHeader: string;
};

export type SupportedLocale = "en-AU" | "en-GB" | "en-US";

export const DATE_FORMATS: Record<SupportedLocale, DateFormats> = {
  "en-AU": {
    short: "DD/MM/YYYY",
    shortDateTime: "DD/MM/YYYY HH:mm:ss",
    readable: "DD/MM/YYYY",
    readableDateTime: "hh:mma DD/MM/YYYY",
    readableDateTimeFilename: "YYYYMMDD-HHmm",
    columnHeader: "DD\u00A0/\u00A0MM\u00A0/\u00A0YYYY",
    /** Short date for chart axes / tooltips \u2014 no year. */
    chartDate: "D MMM",
    monthYear: "MM/YYYY",
    /** Day-of-week header for Detail view grouping. */
    dayHeader: "ddd DD/MM/YYYY",
  },
  "en-GB": {
    short: "DD/MM/YYYY",
    shortDateTime: "DD/MM/YYYY HH:mm:ss",
    readable: "DD/MM/YYYY",
    readableDateTime: "hh:mma DD/MM/YYYY",
    readableDateTimeFilename: "YYYYMMDD-HHmm",
    columnHeader: "DD\u00A0/\u00A0MM\u00A0/\u00A0YYYY",
    chartDate: "D MMM",
    monthYear: "MM/YYYY",
    dayHeader: "ddd DD/MM/YYYY",
  },
  "en-US": {
    short: "MM/DD/YYYY",
    shortDateTime: "MM/DD/YYYY HH:mm:ss",
    readable: "MM/DD/YYYY",
    readableDateTime: "hh:mma MM/DD/YYYY",
    readableDateTimeFilename: "YYYYMMDD-HHmm",
    columnHeader: "MM\u00A0/\u00A0DD\u00A0/\u00A0YYYY",
    chartDate: "MMM D",
    monthYear: "MM/YYYY",
    dayHeader: "ddd MM/DD/YYYY",
  },
};

export const DEFAULT_LOCALE: SupportedLocale = "en-AU";

export const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  Object.prototype.hasOwnProperty.call(DATE_FORMATS, locale);

export const getDateFormats = (
  locale: string | null | undefined
): DateFormats =>
  (locale && isSupportedLocale(locale) && DATE_FORMATS[locale]) ||
  DATE_FORMATS[DEFAULT_LOCALE];
